import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  from,
  fromEvent,
  map,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';

import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseLayerSearchService } from 'src/app/core/api-gis/services/base-layer-search.service';
import { AppConfigService } from 'src/app/core/services/config.service';
import { TranslationModule } from 'src/app/i18n';
import { MaterialModule } from '../../modules/material/material.module';
import { MapSearchItemTypePipe } from './map-search-item-type.pipe';

@Component({
  selector: 'map-search-box',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslationModule,
    MatTooltipModule,
    MapSearchItemTypePipe,
  ],
  templateUrl: './map-search-box.component.html',
  styleUrls: ['./map-search-box.component.scss'],
})
export class MapSearchBoxComponent implements OnInit {
  //#region properties
  readonly searchInput = viewChild<ElementRef>('mapSearchInput');

  searchText: string = '';
  filteredOptions: any[];
  loadingPublic: boolean = false;
  myControl = new FormControl('');

  //infinite scroll
  readonly statesAutocompleteRef = viewChild<MatAutocomplete>('auto');
  readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);
  pageNumber: number = 1;
  pageSize: number = 10;
  useMapIr = AppConfigService.config.useMapIr;
  mapIrXApiKey = AppConfigService.config.mapIrXApiKey;
  @Output() search: EventEmitter<mapSearchOptionDto> =
    new EventEmitter<mapSearchOptionDto>();
  //#endregion

  constructor(private baseLayerService: BaseLayerSearchService) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    fromEvent(this.searchInput()?.nativeElement, 'input')
      .pipe(
        map((event) => event as KeyboardEvent),
        map((event) => (event.target as HTMLInputElement).value),
        map((value) => value.trim()),
        map((value) => (this.searchText = value)),
        distinctUntilChanged(),
        debounceTime(1000),
        switchMap((searchText) => this.searchApi())
      )
      .subscribe((result: any) => {
        this.filteredOptions = [];

        if (!result) return;

        //map.ir
        if (this.useMapIr) {
          const searchedItems = result.value;
          searchedItems.forEach((item) => {
            this.filteredOptions.push({
              id: item.id,
              latitude: item?.geom?.coordinates[1],
              longitude: item?.geom?.coordinates[0],
              title: item.title,
              coordinates: [],
              address: item.address,
              type: item.city,
            });
          });
        }
        //gis
        else {
          const searchedItems = JSON.parse(result as any) as any[];
          searchedItems.forEach((item) => {
            let coordinates;
            if (item?.location?.coordinates[0]?.length)
              coordinates = item?.location?.coordinates[0];
            else coordinates = item?.location?.coordinates;
            this.filteredOptions.push({
              id: item.id,
              latitude: coordinates[1],
              longitude: coordinates[0],
              title: item.name,
              coordinates: coordinates,
              address: item.region,
              type: item.type,
            });
          });
        }
      });
  }

  //search method
  searchApi() {
    if (!this.searchText.trim()) return of([]);
    this.loadingPublic = true;

    // gis
    if (!this.useMapIr)
      return this.baseLayerService
        .gisBaseLayerSearchPost({
          body: {
            searchTerm: this.searchText,
          },
        })
        .pipe(finalize(() => (this.loadingPublic = false)));

    // map.ir
    if (this.useMapIr) {
      return from(
        fetch('https://map.ir/search/v2/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.mapIrXApiKey,
          },
          body: JSON.stringify({ text: this.searchText }),
        }).then((response) => response.json())
      ).pipe(finalize(() => (this.loadingPublic = false)));
    }
  }

  trackByFunc(index: number, item: any) {
    return item?.id;
  }

  emitSelectedPlace(place: mapSearchOptionDto) {
    this.search.emit(place);
  }

  stopPropagationInput(event) {
    if (event.which === 13) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  //#region infinite scroll

  //infinite scroll
  onScrollDown() {
    setTimeout(() => {
      const statesAutocompleteRef = this.statesAutocompleteRef();
      const autocompleteTrigger = this.autocompleteTrigger();
      if (
        statesAutocompleteRef &&
        autocompleteTrigger &&
        statesAutocompleteRef.panel
      ) {
        fromEvent(statesAutocompleteRef.panel.nativeElement, 'scroll')
          .pipe(
            map(
              (x) => this.statesAutocompleteRef().panel.nativeElement.scrollTop
            ),
            takeUntil(autocompleteTrigger.panelClosingActions)
          )
          .subscribe((x) => {
            const scrollTop =
              this.statesAutocompleteRef().panel.nativeElement.scrollTop;
            const scrollHeight =
              this.statesAutocompleteRef().panel.nativeElement.scrollHeight;
            const elementHeight =
              this.statesAutocompleteRef().panel.nativeElement.clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) this.pageSize += 10;
          });
      }
    });
  }

  //#endregion
}

export class mapSearchOptionDto {
  id?: number;
  latitude?: number;
  longitude?: number;
  title?: string;
  coordinates: [];
  address: string;
  type: string;
}
